/**
 * @file Parent-board modal submit orchestration.
 */

import { state as configuredState } from "../../app/state/state.js";
import { renderDetailsView as configuredRenderDetailsView } from "../../app/detail/detailController.js";
import {
    applyParentItemFormData,
    maybeApplyChecklistTemplates,
    saveStandardParentItem,
} from "../api/parentItems.js";
import {
    getCollectionName as configuredGetCollectionName,
} from "../utils/itemLookup.js";
import {
    formatContentForSave as configuredFormatContentForSave,
} from "../utils/contentUtils.js";
import {
    formatDate as configuredFormatDate,
} from "../utils/dateUtils.js";
import { openConfirmationModal as configuredOpenConfirmationModal } from "../ui/confirmationModal.js";
import {
    getRegisteredControl,
    mergeRegisteredControlValues,
    validateRegisteredControls,
} from "../ui/controls/controlCore.js";
import { getDynamicInputLabelText } from "./dynamicFields.js";
import {
    getParentFormAdapter,
    isKnownParentFormType,
} from "./parentFormRegistry.js";
import { formItemData } from "./formModalState.js";
import { closeForm as configuredCloseForm } from "./modalShell.js";

/**
 * Checks whether a modal form type is a parent-board item.
 * @param {string} type Form type.
 * @returns {boolean} Whether the form type is parent-board owned.
 */
export function isParentFormType(type) {
    return isKnownParentFormType(type);
}

function shouldRequireRecurrenceAnchor(frequency, modifier) {
    if (frequency === "daily" || frequency === "yearly") {return true;}
    if (frequency === "weekly") {return modifier === "same_day";}
    if (frequency === "monthly") {return modifier === "same_date";}
    return false;
}

/**
 * Builds a formatted data object from a form.
 * @param {HTMLFormElement} form Source form.
 * @param {Function} formatContentForSave Text formatting helper.
 * @returns {{data: object, formData: FormData}} Form data and shaped payload.
 */
function getFormattedFormData(form, formatContentForSave) {
    const formData = new FormData(form);
    const data = {};
    for (const [key, value] of formData.entries()) {
        data[key] = typeof value === "string"
            ? formatContentForSave(value)
            : value;
    }
    return { data, formData };
}

function focusFirstRegisteredControlError(validation) {
    const firstError = validation.errors[0];
    const handle = firstError ? getRegisteredControl(firstError.id) : null;
    const focusTarget = handle?.root?.querySelector?.(
        "button, [tabindex], input, select, textarea",
    );

    if (typeof focusTarget?.focus === "function") {
        focusTarget.focus({ preventScroll: true });
    }

    handle?.root?.scrollIntoView?.({
        block: "center",
        behavior: "smooth",
    });
}

/**
 * Merges active registered-control values over the legacy FormData payload.
 * @param {object} params Parent form extraction context.
 * @param {HTMLFormElement} params.form Active parent form.
 * @param {object} params.data Existing FormData-derived payload.
 * @returns {{data: object, valid: boolean}} Merged payload and validation status.
 */
function mergeParentRegisteredControlData({ form, data }) {
    const validation = validateRegisteredControls(form);
    if (!validation.valid) {
        focusFirstRegisteredControlError(validation);
        return {
            data,
            valid: false,
        };
    }

    return {
        data: mergeRegisteredControlValues(data, form),
        valid: true,
    };
}

/**
 * Appends dynamic-field markdown to the parent-board text field.
 * @param {object} params Dynamic-field context.
 * @returns {void}
 */
function applyDynamicFieldText({
    data,
    form,
    formatDate,
    type,
}) {
    const dynamicAnchor = form.querySelector("#dynamic-fields-anchor");
    if (!dynamicAnchor) {return;}

    let dynamicText = "";
    const dynamicKeysToRemove = [];
    const containers = dynamicAnchor.querySelectorAll("[id^=\"dynamic-fields-\"]");

    containers.forEach((container) => {
        const header = container.querySelector("h5")?.textContent ||
            "Additional Details";
        const inputs = container.querySelectorAll("input, select, textarea");
        const sectionParts = [];
        let hasValidInput = false;

        inputs.forEach((input) => {
            const name = input.name;
            if (!name) {return;}

            dynamicKeysToRemove.push(name);

            let value = input.value;
            let labelText;

            if (input.type === "checkbox") {
                value = input.checked ? "Yes" : "No";
            }

            if ((input.type === "date" || input.type === "datetime-local") &&
                value) {
                value = formatDate(value);
            }

            if (value && value.trim() !== "") {
                labelText = getDynamicInputLabelText(input, name);

                const lines = value.split("\n");
                sectionParts.push(`  **${labelText}**: ${lines[0]}`);
                for (let i = 1; i < lines.length; i++) {
                    sectionParts.push(`  ${lines[i]}`);
                }
                hasValidInput = true;
            }
        });

        if (hasValidInput) {
            dynamicText += `\n**${header}**\n${sectionParts.join("\n")}`;
        }
    });

    dynamicKeysToRemove.forEach((key) => delete data[key]);

    if (!dynamicText) {return;}

    const targetField = type === "task" ?
        "description" :
        (type === "maintenance" || type === "lab") ?
            "details" :
            "comments";
    const currentContent = data[targetField] || "";
    data[targetField] = type === "maintenance" && currentContent
        ? `${dynamicText.trimStart()}\n\n${currentContent}`
        : currentContent
        ? `${currentContent}\n${dynamicText}`
        : dynamicText.trimStart();
}

/**
 * Applies task recurrence fields from the modal payload.
 * @param {object} params Recurrence context.
 * @returns {boolean} Whether recurrence validation passed.
 */
function applyTaskRecurrence({
    data,
    openConfirmationModal,
    type,
}) {
    if (type !== "task") {return true;}

    const recurrenceEnabled = data.recurrence_enabled === "true";
    const recurrenceFrequency = data.recurrence_frequency || null;
    const recurrenceAnchor = data.recurrence_anchor || null;
    let recurrenceModifier = data.recurrence_modifier || null;

    delete data.recurrence_enabled;
    delete data.recurrence_frequency;
    delete data.recurrence_modifier;
    delete data.recurrence_anchor;

    if (!recurrenceEnabled) {
        data.recurrence = {
            frequency: null,
            modifier: null,
            anchor: null,
        };
        return true;
    }

    if (!data.due_date) {
        openConfirmationModal(
            "Due Date Required",
            "Recurring tasks require a due date.",
            async () => { },
            false,
            "OK",
        );
        return false;
    }

    if (!recurrenceFrequency) {
        openConfirmationModal(
            "Repeat Rule Required",
            "Choose how often this task repeats.",
            async () => { },
            false,
            "OK",
        );
        return false;
    }

    if (shouldRequireRecurrenceAnchor(
        recurrenceFrequency,
        recurrenceModifier,
    ) && !recurrenceAnchor) {
        openConfirmationModal(
            "Calculation Rule Required",
            "Choose what the recurrence should be calculated from.",
            async () => { },
            false,
            "OK",
        );
        return false;
    }

    if ((recurrenceFrequency === "weekly" ||
        recurrenceFrequency === "monthly") &&
        !recurrenceModifier) {
        openConfirmationModal(
            "Repeat Rule Required",
            "Choose the recurrence rule for this task.",
            async () => { },
            false,
            "OK",
        );
        return false;
    }

    if (recurrenceFrequency !== "weekly" &&
        recurrenceFrequency !== "monthly") {
        recurrenceModifier = null;
    }

    data.recurrence = {
        frequency: recurrenceFrequency,
        modifier: recurrenceModifier,
        anchor: shouldRequireRecurrenceAnchor(
            recurrenceFrequency,
            recurrenceModifier,
        ) ? recurrenceAnchor : null,
    };
    return true;
}

/**
 * Creates the parent-board modal submit handler.
 * @param {object} deps Dependency bag.
 * @returns {Function} Parent-board submit handler.
 */
export function createParentFormSubmitHandler({
    closeForm,
    formItemDataProvider,
    formatContentForSave,
    formatDate,
    getCollectionName,
    openConfirmationModal,
    parentItemsApi,
    renderDetailsView,
    state,
}) {
    return async function handleParentFormSubmit({
        form,
        id,
        parentId,
        setSubmitProcessingState,
        type,
    }) {
        const { data: legacyData } = getFormattedFormData(form, formatContentForSave);
        const mergedPayload = mergeParentRegisteredControlData({
            form,
            data: legacyData,
        });
        if (!mergedPayload.valid) {return;}

        const data = mergedPayload.data;
        const formAdapter = getParentFormAdapter(type);

        applyDynamicFieldText({
            data,
            form,
            formatDate,
            type,
        });

        const recurrenceValid = applyTaskRecurrence({
            data,
            openConfirmationModal,
            type,
        });
        if (!recurrenceValid) {return;}

        const shouldContinueSubmit = await formAdapter?.prepareSubmit?.({
            data,
            form,
            formItemData: formItemDataProvider(),
            id,
            parentId,
            type,
        });
        if (shouldContinueSubmit === false) {return;}

        formAdapter?.applySubmitData?.({
            data,
            form,
            formItemData: formItemDataProvider(),
            id,
            parentId,
            type,
        });

        try {
            setSubmitProcessingState(true, "Saving...");
            const adapterSaveItem =
                typeof formAdapter?.saveItem === "function" ?
                    formAdapter.saveItem :
                    null;
            if (!adapterSaveItem) {
                parentItemsApi.applyParentItemFormData({
                    data,
                    formItemData: formItemDataProvider(),
                    id,
                    parentId,
                    type,
                });
            }
            await parentItemsApi.maybeApplyChecklistTemplates({ data, id, type });
            let saveResult = null;
            if (adapterSaveItem) {
                saveResult = await adapterSaveItem({
                    data,
                    form,
                    formItemData: formItemDataProvider(),
                    id,
                    parentId,
                    type,
                });
            } else {
                saveResult = await parentItemsApi.saveStandardParentItem({
                    collectionName: getCollectionName(type),
                    data,
                    id,
                });
            }
            if (saveResult === false) {
                setSubmitProcessingState(false);
                return;
            }
            formAdapter?.afterSave?.({
                data,
                form,
                formItemData: formItemDataProvider(),
                id,
                parentId,
                saveResult,
                type,
            });

            closeForm();

            if (state.selectedItemId &&
                (id === state.selectedItemId || parentId === state.selectedItemId)) {
                renderDetailsView();
            } else if (parentId && state.selectedItemId === parentId) {
                renderDetailsView();
            }
        } catch (error) {
            console.error("Error saving parent form data:", error);
            setSubmitProcessingState(false);
            const errorMessage = (error.code === "internal" || !error.message)
                ? "Something went wrong. Please try again later."
                : `There was an error saving: ${error.message}. Please check console.`;
            openConfirmationModal(
                "Error",
                errorMessage,
                async () => { },
                false,
                "OK",
            );
        }
    };
}

export const handleParentFormSubmit = createParentFormSubmitHandler({
    closeForm: configuredCloseForm,
    formItemDataProvider: () => formItemData,
    formatContentForSave: configuredFormatContentForSave,
    formatDate: configuredFormatDate,
    getCollectionName: configuredGetCollectionName,
    openConfirmationModal: configuredOpenConfirmationModal,
    parentItemsApi: {
        applyParentItemFormData,
        maybeApplyChecklistTemplates,
        saveStandardParentItem,
    },
    renderDetailsView: configuredRenderDetailsView,
    state: configuredState,
});
